import { Routes, Route, RoutesProps, Navigate } from "react-router-dom";

import React, { Suspense, useEffect } from "react";
import { GoogleOAuthProvider } from "@react-oauth/google";
import ProtectedRoute from "./ProtectedRoute";
import Setting from "../components/Settings/Setting";
import { HOME_ROUTE } from "../components/Common/Breadcrumbs/PageBreadcrumb";
import { AccessDenied } from "../components/Common/AccessDenied/AccessDenied";
import AdGroups from "../components/AmazonAds/Campaigns/AdGroups";
import {
  CardGridSkeleton,
  FormSkeleton,
  PageSkeleton,
  Skeleton,
  SkeletonRegion,
} from "../components/Common/Skeleton/Skeleton";
const Main = React.lazy(() => import("../components/Main"));
const AuthRedirect = React.lazy(
  () => import("../components/AuthRedirect/AuthRedirect"),
);
const Profile = React.lazy(() => import("../components/Profile/Profile"));
const Brands = React.lazy(() => import("../components/Brands/Brands"));
const TeamMember = React.lazy(
  () => import("../components/TeamMember/TeamMember"),
);
const AddBrand = React.lazy(
  () => import("../components/Brands/AddBrand/AddBrand"),
);
const MySubscription = React.lazy(
  () => import("../components/Subscription/Subscription"),
);

const AmazonMain = React.lazy(
  () => import("../components/AmazonAds/AmazonMain"),
);
const AmazonAds = React.lazy(() => import("../components/AmazonAds/AmazonAds"));
const AuditLogs = React.lazy(
  () => import("../components/AmazonAds/AuditLogs/AuditLogs"),
);
const SearchTerm = React.lazy(
  () => import("../components/Reports/SearchTearm/SearchTerms"),
);
const SPPlacementReport = React.lazy(
  () => import("../components/AmazonAds/Campaigns/Common/placements"),
);
const SPAutoTargetReport = React.lazy(
  () => import("../components/AmazonAds/Campaigns/Common/Targeting"),
);
const SBPlacementReport = React.lazy(
  () => import("../components/AmazonAds/Campaigns/Common/SBPlacements"),
);
const CampaignReport = React.lazy(
  () => import("../components/Reports/Campaign/Campaign"),
);
const Keywords = React.lazy(() => import("../components/Keywords/Keywords"));

const Heatmaps = React.lazy(() => import("../components/Heatmaps/Heatmaps"));

const AccountRedirect = React.lazy(
  () =>
    import("../components/MarketingInsights/AccountRedirect/AccountRedirect"),
);
const Budget = React.lazy(() => import("../pages/Budget/Budget"));

const CampaignDetails = React.lazy(
  () => import("../components/AmazonAds/Campaigns/CampaignDetails"),
);

const AdsDetails = React.lazy(
  () => import("../components/AmazonAds/Campaigns/AdsDetails"),
);
const ConnectedChannels = React.lazy(
  () =>
    import("../components/MarketingInsights/CampaignsPerformance/ConnectedChannels/ConnectedChannels"),
);
const AddProductStep1 = React.lazy(
  () => import("../components/AmazonAds/Products/AddProduct/Step1"),
);
const AddProductStep2 = React.lazy(
  () => import("../components/AmazonAds/Products/AddProduct/Step2"),
);
const AddCampaignSteps = React.lazy(
  () => import("../components/AmazonAds/Campaigns/AddCampaign/Steps"),
);
const AddCampaign = React.lazy(
  () => import("../components/AmazonAds/Campaigns/AddCampaign/AddCampaign"),
);

const SDCreate = React.lazy(
  () => import("../components/AmazonAds/Campaigns/CreateCampaign/sd/SDCreate"),
);

const SBCreate = React.lazy(
  () => import("../components/AmazonAds/Campaigns/CreateCampaign/sb/SBCreate"),
);

const Accounts = React.lazy(
  () => import("../components/MainAccounts/MainAccounts"),
);

const WalmartAccounts = React.lazy(
  () => import("../components/WalmartAccounts/WalmartAccounts"),
);

const PortfolioList = React.lazy(
  () => import("../components/AmazonAccounts/Portfolio/PortfolioList"),
);

const WalmartPortfolioList = React.lazy(
  () => import("../components/WalmartAccounts/Portfolio/Portfolio"),
);

const ProductReport = React.lazy(
  () => import("../components/Reports/Product/ProductPage"),
);

const Products = React.lazy(
  () => import("../components/Reports/Product/Products"),
);


const Login = React.lazy(() => import("../components/Login/Login"));

const Register = React.lazy(() => import("../components/Login/Register"));

const ForgotPassword = React.lazy(
  () => import("../components/Login/ForgotPassword"),
);

const ResetPassword = React.lazy(
  () => import("../components/Login/ResetPassword"),
);

const BrandInvitation = React.lazy(
  () => import("../components/Brands/BrandInvitation"),
);

const SBProductAds = React.lazy(
  () => import("../components/Reports/Product/SBProductAds"),
);

const Recommendations = React.lazy(
  () => import("../components/Recommendations/Recommendations"),
);

const WalmartMain = React.lazy(
  () => import("../components/WalmartAds/WalmartMain"),
);

const WalmartAds = React.lazy(
  () => import("../components/WalmartAds/WalmartAds"),
);

const WalmartCampaignDetails = React.lazy(
  () => import("../components/WalmartAds/Campaigns/CampaignDetails"),
);

const WalmartAdGroups = React.lazy(
  () => import("../components/WalmartAds/Campaigns/WalmartAdGroups"),
);

const WalmartAddCampaign = React.lazy(
  () => import("../components/WalmartAds/Campaigns/AddCampaign/AddCampaign"),
);

const WalmartAdsDetails = React.lazy(
  () => import("../components/WalmartAds/Campaigns/WalmartAdsDetails"),
);

const Dashboard = React.lazy(() => import("../components/Dashboard/Dashboard"));

const AutoLogin = React.lazy(() => import("../components/Login/AutoLogin"));

const AmazonDashboard = React.lazy(
  () => import("../components/AmazonAds/Dashboard/AmazonDashboard"),
);

const HourlyPerformance = React.lazy(
  () =>
    import("../components/AmazonAds/Hourly Performancepage/Hourlyperformancepage"),
);

// What a screen shows while its own JS chunk downloads. React.lazy keeps a
// module once it has loaded, so this is a first-visit frame only. Each part
// stands where the screen's own first frame puts it, so the screen's own
// loading state takes over in place.
type FallbackBody = "table" | "overview" | "chart" | "cards" | "form";

const OVERVIEW_METRICS = [0, 1, 2, 3, 4, 5, 6];

const fallbackBody = (body: FallbackBody) => {
  if (body === "form")
    return (
      <div className="wmx-skeleton-card p-4">
        <FormSkeleton />
      </div>
    );
  return (
    <>
      {/* The `table-filtr-wrap` heading row: title left, actions right. */}
      <div
        className="d-flex align-items-center justify-content-between mb-3 wmx-skeleton-on-grey"
        style={{ height: 38 }}
      >
        <Skeleton width={180} height={16} />
        <Skeleton width={120} height={38} />
      </div>
      {body === "cards" && <CardGridSkeleton count={8} />}
      {/* OverviewCards' metric row ("chart": every screen with a chart has
          one above it). `.overview-card` is styled only in `.amazonads-page`. */}
      {(body === "overview" || body === "chart") && (
        <div className="overview-card d-flex mb-3">
          {OVERVIEW_METRICS.map((m) => (
            <div key={m} className="flex-fill d-flex flex-column align-items-center">
              <Skeleton width="40%" style={{ margin: "4.5px 0" }} />
              <Skeleton width="60%" height={20} style={{ margin: "3.5px 0" }} />
            </div>
          ))}
        </div>
      )}
      {body !== "cards" && (
        <PageSkeleton breadcrumb={false} chart={body === "chart"} />
      )}
    </>
  );
};

// A screen under AmazonMain / WalmartMain: the layout draws the strip and the
// container, so this is the body alone. `tabs` is a detail screen's first
// frame: its back-arrow "Campaign Details" line over the vertical tab rail.
const ScreenFallback = ({
  body = "table",
  tabs = 0,
}: {
  body?: FallbackBody;
  tabs?: number;
}) => (
  <SkeletonRegion>
    {tabs > 0 ? (
      <>
        <div
          className="d-flex align-items-center wmx-skeleton-on-grey"
          style={{ height: 24 }}
        >
          <Skeleton width={160} height={16} />
        </div>
        <PageSkeleton breadcrumb={false} tabs={tabs} tabsLayout="side" />
      </>
    ) : (
      fallbackBody(body)
    )}
  </SkeletonRegion>
);

// A screen directly under Main draws its own `.app-body` and breadcrumb strip
// (one 18 px crumb line), so its fallback draws them too.
const PageFallback = ({ body = "table" }: { body?: FallbackBody }) => (
  <div className="app-body">
    <SkeletonRegion>
      <section className="page-breadcums wmx-skeleton-on-grey">
        <div style={{ padding: "3px 0" }}>
          <Skeleton width={140} />
        </div>
      </section>
      <div className="container-fluid">{fallbackBody(body)}</div>
    </SkeletonRegion>
  </div>
);

// AmazonMain / WalmartMain: their strip (crumb, profile and portfolio selects,
// date range) over the body of Campaigns, the screen a channel is entered on.
// Their child screens' fallbacks are the body alone.
const ChannelFallback = () => (
  <div className="app-body">
    <SkeletonRegion>
      <section className="page-breadcums wmx-skeleton-on-grey">
        <div className="row align-items-center">
          <div className="col-md-4">
            <Skeleton width={140} />
          </div>
          <div className="col-md-8 d-flex justify-content-end align-items-center">
            <div className="col-md-6 me-2">
              <Skeleton height={38} radius={4} />
            </div>
            <div className="col-md-3 me-2">
              <Skeleton height={38} radius={4} />
            </div>
            <div className="col-md-3">
              <Skeleton height={14} />
            </div>
          </div>
        </div>
      </section>
      <div className="container-fluid amazonads-page">
        {fallbackBody("chart")}
      </div>
    </SkeletonRegion>
  </div>
);

const Navigation = () => {
  useEffect(() => {}, []);

  interface ProtectedRouteProps extends RoutesProps {
    authenticationPath: string;
  }
  const defaultProtectedRouteProps: Omit<ProtectedRouteProps, "outlet"> = {
    authenticationPath: "/login",
  };

  return (
    <>
      <div id="main-wraper">
        <Routes>
          <Route
            path="/login"
            element={
              <Suspense fallback={<></>}>
                <Login />
              </Suspense>
            }
          />
          <Route
            path="/auth/redirect"
            element={
              <Suspense fallback={<></>}>
                <AutoLogin />
              </Suspense>
            }
          />
          <Route
            path="/register"
            element={
              <Suspense fallback={<></>}>
                <Register />
              </Suspense>
            }
          />
          <Route
            path="/forgot-password"
            element={
              <Suspense fallback={<></>}>
                <ForgotPassword />
              </Suspense>
            }
          />
          <Route
            path="/reset-password/:token"
            element={
              <Suspense fallback={<></>}>
                <ResetPassword />
              </Suspense>
            }
          />
          <Route
            path="/auth/redirect"
            element={
              <Suspense fallback={<></>}>
                <AuthRedirect />
              </Suspense>
            }
          />
          <Route
            path="/setup"
            element={
              <Suspense fallback={<></>}>
                <AddBrand />
              </Suspense>
            }
          />
          <Route
            path="/invitation"
            element={
              <ProtectedRoute
                {...defaultProtectedRouteProps}
                outlet={
                  <Suspense fallback={<></>}>
                    <BrandInvitation />
                  </Suspense>
                }
              />
            }
          />
          <Route
            path="/accounts"
            element={
              <ProtectedRoute
                {...defaultProtectedRouteProps}
                outlet={
                  <Suspense fallback={<></>}>
                    <Accounts />
                  </Suspense>
                }
              />
            }
          />

          <Route
            path="/walmart-accounts"
            element={
              <ProtectedRoute
                {...defaultProtectedRouteProps}
                outlet={
                  <Suspense fallback={<></>}>
                    <WalmartAccounts />
                  </Suspense>
                }
              />
            }
          />

          <Route
            path="/"
            element={
              <ProtectedRoute
                {...defaultProtectedRouteProps}
                outlet={
                  <Suspense fallback={<></>}>
                    <Main />
                  </Suspense>
                }
              />
            }
          >
            {/* "/" alone matches only the layouts and renders an empty page;
                send it to Home, where login lands too. */}
            <Route index element={<Navigate to={HOME_ROUTE} replace />} />
            <Route
              path="/profile"
              element={
                <Suspense fallback={<PageFallback body="form" />}>
                  <Profile />
                </Suspense>
              }
            />
            <Route
              path="/my-brand"
              element={
                <Suspense fallback={<PageFallback />}>
                  <Brands />
                </Suspense>
              }
            />
            <Route
              path="/team"
              element={
                <Suspense fallback={<PageFallback />}>
                  <TeamMember />
                </Suspense>
              }
            />
            <Route
              path="/my-subscription"
              element={
                <Suspense fallback={<PageFallback body="form" />}>
                  <MySubscription />
                </Suspense>
              }
            />
            <Route
              path="/setting"
              element={
                <Suspense fallback={<PageFallback body="form" />}>
                  <Setting />
                </Suspense>
              }
            />

            <Route
              path="/access-denied"
              element={
                <Suspense fallback={<></>}>
                  <AccessDenied />
                </Suspense>
              }
            />
            <Route
              path="connected-channels"
              element={
                <Suspense fallback={<PageFallback />}>
                  <ConnectedChannels />
                </Suspense>
              }
            />
            <Route
              path="campaign-report"
              element={
                <Suspense fallback={<PageFallback />}>
                  <CampaignReport />
                </Suspense>
              }
            />

            <Route
              path="/"
              element={
                <Suspense fallback={<ChannelFallback />}>
                  <AmazonMain />
                </Suspense>
              }
            >
              <Route
                path="/heatmaps"
                element={
                  <Suspense fallback={<ScreenFallback body="chart" />}>
                    <Heatmaps />
                  </Suspense>
                }
              />
              <Route
                path="amazonads"
                element={
                  <Suspense fallback={<ScreenFallback body="chart" />}>
                    <AmazonAds />
                  </Suspense>
                }
              />
              <Route
                path="audit-logs"
                element={
                  <Suspense fallback={<ScreenFallback />}>
                    <AuditLogs />
                  </Suspense>
                }
              />
              <Route
                path="ad-groups"
                element={
                  <Suspense fallback={<ScreenFallback body="chart" />}>
                    <AdGroups />
                  </Suspense>
                }
              />
              <Route
                path="product-report"
                element={
                  <Suspense fallback={<ScreenFallback body="chart" />}>
                    <ProductReport />
                  </Suspense>
                }
              />
              <Route
                path="products"
                element={
                  <Suspense fallback={<ScreenFallback />}>
                    <Products />
                  </Suspense>
                }
              />
              {/* The detail screens' tab counts on a first load (the SP set). */}
              <Route
                path="campaign-details/:id"
                element={
                  <Suspense fallback={<ScreenFallback tabs={6} />}>
                    <CampaignDetails />
                  </Suspense>
                }
              />
              <Route
                path="ad-group-details"
                element={
                  <Suspense fallback={<ScreenFallback tabs={5} />}>
                    <AdsDetails />
                  </Suspense>
                }
              />
              <Route
                path="ad-group-details/:id"
                element={
                  <Suspense fallback={<ScreenFallback tabs={5} />}>
                    <AdsDetails />
                  </Suspense>
                }
              />

              <Route
                path="budget"
                element={
                  <Suspense fallback={<ScreenFallback />}>
                    <Budget />
                  </Suspense>
                }
              />
              <Route
                path="keywords"
                element={
                  <Suspense fallback={<ScreenFallback />}>
                    <Keywords />
                  </Suspense>
                }
              />
              <Route
                path="search-term"
                element={
                  <Suspense fallback={<ScreenFallback body="overview" />}>
                    <SearchTerm />
                  </Suspense>
                }
              />
              <Route
                path="sp-placement-report"
                element={
                  <Suspense fallback={<ScreenFallback body="overview" />}>
                    <SPPlacementReport ctype="ALL" />
                  </Suspense>
                }
              />
              <Route
                path="auto-target-report"
                element={
                  <Suspense fallback={<ScreenFallback body="overview" />}>
                    <SPAutoTargetReport target_type="SP_TARGET" type="AUTO" />
                  </Suspense>
                }
              />
              <Route
                path="targeting-report"
                element={
                  <Suspense fallback={<ScreenFallback body="overview" />}>
                    <SPAutoTargetReport target_type="" type="MANUAL" />
                  </Suspense>
                }
              />
              <Route
                path="sb-product-ads"
                element={
                  <Suspense fallback={<ScreenFallback body="overview" />}>
                    <SBProductAds />
                  </Suspense>
                }
              />
              <Route
                path="recommendations"
                element={
                  <Suspense fallback={<ScreenFallback />}>
                    <Recommendations />
                  </Suspense>
                }
              />

              <Route
                path="sb-placement-report"
                element={
                  <Suspense fallback={<ScreenFallback body="overview" />}>
                    <SBPlacementReport />
                  </Suspense>
                }
              />
            </Route>

            <Route
              path="/"
              element={
                <Suspense fallback={<ChannelFallback />}>
                  <WalmartMain />
                </Suspense>
              }
            >
              <Route
                path="/walmartads"
                element={
                  <Suspense fallback={<ScreenFallback body="chart" />}>
                    <WalmartAds />
                  </Suspense>
                }
              />

              <Route
                path="/walmart-adgroups"
                element={
                  <Suspense fallback={<ScreenFallback body="chart" />}>
                    <WalmartAdGroups />
                  </Suspense>
                }
              />

              <Route
                path="/walmart-campaign-details/:id"
                element={
                  <Suspense fallback={<ScreenFallback tabs={2} />}>
                    <WalmartCampaignDetails />
                  </Suspense>
                }
              />

              <Route
                path="walmart-adgroup-details/:adGroupId/:campaignId"
                element={
                  <Suspense fallback={<ScreenFallback tabs={2} />}>
                    <WalmartAdsDetails />
                  </Suspense>
                }
              />
            </Route>

            <Route
              path="walmartads/add-campaign"
              element={
                <Suspense fallback={<PageFallback body="form" />}>
                  <WalmartAddCampaign />
                </Suspense>
              }
            />

            <Route
              path="amazonads/add-product/step1"
              element={
                <Suspense fallback={<PageFallback body="form" />}>
                  <AddProductStep1 />
                </Suspense>
              }
            />
            <Route
              path="amazonads/add-product/step2"
              element={
                <Suspense fallback={<PageFallback body="form" />}>
                  <AddProductStep2 />
                </Suspense>
              }
            />
            <Route
              path="amazonads/add-campaign"
              element={
                <Suspense fallback={<PageFallback body="form" />}>
                  <AddCampaign />
                </Suspense>
              }
            />
            <Route
              path="amazonads/add-campaign/steps"
              element={
                <Suspense fallback={<PageFallback body="form" />}>
                  <AddCampaignSteps />
                </Suspense>
              }
            />
            <Route
              path="amazonads/add-campaign/sd"
              element={
                <Suspense fallback={<PageFallback body="form" />}>
                  <SDCreate />
                </Suspense>
              }
            />
            <Route
              path="amazonads/add-campaign/sb"
              element={
                <Suspense fallback={<PageFallback body="form" />}>
                  <SBCreate />
                </Suspense>
              }
            />
            <Route
              path="amazon/oauth/redirect"
              element={
                <Suspense fallback={<></>}>
                  <AccountRedirect linkType={"AMAZON"} />
                </Suspense>
              }
            />
            <Route
              path="amazon/sp-api/oauth/redirect"
              element={
                <Suspense fallback={<></>}>
                  <AccountRedirect linkType={"AMAZON_SPAPI"} />
                </Suspense>
              }
            />
            <Route
              path="/portfolios"
              element={
                <Suspense fallback={<PageFallback />}>
                  <PortfolioList />
                </Suspense>
              }
            />
            <Route
              path="/amazon-dashboard"
              element={
                <Suspense fallback={<PageFallback body="cards" />}>
                  <AmazonDashboard />
                </Suspense>
              }
            />
            {/* <Route
              path="/hourly-performance"
              element={
                <Suspense fallback={<PageFallback />}>
                  <HourlyPerformance />
                </Suspense>
              }
            /> */}
            <Route
              path="/walmart-portfolios"
              element={
                <Suspense fallback={<PageFallback />}>
                  <WalmartPortfolioList />
                </Suspense>
              }
            />
            <Route
              path="/dashboard"
              element={
                <Suspense fallback={<PageFallback body="cards" />}>
                  <Dashboard />
                </Suspense>
              }
            />
          </Route>
          <Route path="*" element={<Navigate to={HOME_ROUTE} replace />} />
        </Routes>
      </div>
    </>
  );
};

export default Navigation;
